/**
 * Three small notices the offer card, the menu and the summary share.
 *
 * They live here rather than in any one section because two of them are
 * drawn in two places each — item B4's check lines in the menu and in the
 * fixed bottom bar, item B7's minimum on the offer card and on the summary —
 * and a section importing another section is how an import cycle starts.
 */

import { CONFERENCE, LIGHT_DINNER, OTHER_OCCASION } from '@plt/engine'

import type { PageBundle } from '../bundle.ts'
import {
  MENU_CHECK_STATE_ICON,
  MENU_CHECK_STATE_TEXT,
  type MenuCheckState,
  type SideCheck,
} from '../menuCheck.ts'
import { earlyPickupLine, isReception, minimumOrderOf, replacesMeal } from '../model.ts'

/**
 * Run 17 item B4: *Sós: {current} falat/fő · ajánlott: {recommended}*, with
 * one state carried as **text and an icon, never colour alone**.
 *
 * Drinks are excluded, and so is fruit: both sit outside pieces per guest
 * (§5.6). On a boxed order — *Munkaebéd* — these are the same figures, per
 * guest, which is what item B4 asks for: the figure is per guest already and
 * a box holds one guest's share.
 *
 * One decimal, with the Hungarian decimal comma in Hungarian, through the
 * page's own formatter.
 */
export function MenuCheckLines(props: { page: PageBundle; id: string }): JSX.Element | null {
  const { page, id } = props
  const { t, f, menuCheck } = page
  /* **Run 18 item R18-3(b): no recommendation check on *Egyéb*.** Its
     pieces per guest are a fixed default out of the register, not a
     recommendation, so there is nothing for the customer's menu to be
     measured against — and *megfelelő* would say there was. Suppressed
     here, in the one component both the menu and the mobile bottom bar
     draw, so neither can keep the lines in any state. */
  if (page.state.occasion === OTHER_OCCASION) return null
  const line = (check: SideCheck): JSX.Element => {
    const label = check.side === 'sav' ? t('Sós', 'Savoury') : t('Édes', 'Sweet')
    const state: MenuCheckState = check.state
    return (
      <p
        key={check.side}
        className="sub menucheckline"
        data-menucheck={check.side}
        data-menucheckstate={state}
        style={{ margin: '0 0 4px' }}
      >
        <span className="menucheckfig">
          {t(
            `${label}: ${f.n(check.current, 1)} falat/fő · ajánlott: ${f.n(check.recommended, 1)}`,
            `${label}: ${f.n(check.current, 1)} pieces per guest · recommended: ${f.n(check.recommended, 1)}`,
          )}
        </span>{' '}
        <span className="menucheckstate" data-menucheckword={state}>
          <span className="menucheckicon" aria-hidden="true">
            {MENU_CHECK_STATE_ICON[state]}
          </span>{' '}
          {t.of(MENU_CHECK_STATE_TEXT[state])}
        </span>
      </p>
    )
  }
  return (
    <div id={id} className="menucheck" data-testid="menu-check">
      {line(menuCheck.sav)}
      {line(menuCheck.sw)}
    </div>
  )
}

/**
 * **Run 17 item B7 (DECIDED 20261015, Barna).**
 *
 *   *A minimális rendelési érték {minimum}; még {missing} hiányzik.*
 *   *The minimum order is {minimum}; {missing} to go.*
 *
 * Shown on the offer card and on the summary whenever the **food** total is
 * below the minimum that applies — `min_pickup`, or the zone minimum once a
 * delivery address is known. Both amounts are gross for a consumer and net
 * for a business buyer, as every other amount those two buyers read. The
 * existing delivery-step notice stays where it is.
 */
export function MinimumOrderLine(props: { page: PageBundle; id: string }): JSX.Element | null {
  const { page, id } = props
  const { t, f, state, totals, params } = page
  const minimum = minimumOrderOf({ totals, business: state.business, params })
  if (!minimum.below) return null
  return (
    <p className="note stop offerminimum" id={id} data-minimum={minimum.basis} style={{ margin: '6px 0 0' }}>
      {t(
        `A minimális rendelési érték ${f.money(minimum.minimumShown)}; még ${f.money(minimum.missingShown)} hiányzik.`,
        `The minimum order is ${f.money(minimum.minimumShown)}; ${f.money(minimum.missingShown)} to go.`,
      )}
    </p>
  )
}

/**
 * **Run 19 §4 (DECIDED 20261018, Barna): the meal note, in both answers.**
 *
 * Wherever an order's occasion name is shown, its meal note sits next to it.
 * Run 17 item B11(c) and run 18 item R18-2 wrote the *Nem* half only; a
 * customer reading *Állófogadás* on an offer card could not tell whether the
 * food was a meal, which is the one thing that question decided.
 *
 * | Occasion | *Igen* | *Nem* |
 * | --- | --- | --- |
 * | *Állófogadás* | *Ebédnek/vacsorának is elegendő* | *Könnyű falatok, nem váltja ki az ebédet/vacsorát* |
 * | *Egész napos konferencia* | *Ebédnek is elegendő* | *Könnyű falatok, nem váltja ki az ebédet* |
 * | *Könnyű vacsora* | always *Vacsorának is elegendő* | n/a |
 * | *Munkaebéd*, *Kávészünet*, *Egyéb* | no note | no note |
 *
 * **One source.** `public.meal_replacement_note` writes exactly these words
 * for the kitchen output, the console, every email and every PDF; this is
 * the same table for the page, and the two are pinned against each other in
 * the tests. The English is PROPOSED.
 */
export const mealNote = (page: PageBundle): string | null => {
  const { state } = page
  if (state.occasion === LIGHT_DINNER) {
    return page.t('Vacsorának is elegendő', 'Enough for dinner')
  }
  if (isReception(state.occasion)) {
    return replacesMeal(state.occasion)
      ? page.t('Ebédnek/vacsorának is elegendő', 'Enough for lunch or dinner')
      : page.t(
          'Könnyű falatok, nem váltja ki az ebédet/vacsorát',
          "Light bites, doesn't replace lunch or dinner",
        )
  }
  if (state.occasion === CONFERENCE) {
    return state.conference.lunch
      ? page.t('Ebédnek is elegendő', 'Enough for lunch')
      : page.t('Könnyű falatok, nem váltja ki az ebédet', "Light bites, doesn't replace lunch")
  }
  return null
}

/**
 * **Run 19 §8 (DECIDED 20261016, Barna): a pickup that is not on the event
 * date.**
 *
 * Ágó's test of 16 October found that a collection on a day other than the
 * event day did not stand out, although it is the costliest thing on the
 * page to misread: a customer who arrives on the Saturday for food that was
 * ready on the Friday has no event.
 *
 * A callout with its own background and an icon, set apart from the text
 * around it, and **not red** — red is the page's colour for an error and
 * this is not one. It is drawn wherever the pickup time already is: the
 * fulfilment section, the offer card and the summary.
 *
 * `null` where the collection is on the event date, which is every ordinary
 * order.
 */
export function PickupCallout(props: { page: PageBundle; id: string }): JSX.Element | null {
  const { page, id } = props
  const line = earlyPickupLine({
    state: page.state,
    calendar: page.calendar,
    param: page.param,
    lang: page.state.lang,
  })
  if (line === null) return null
  return (
    <div className="note pickupalert" id={id} data-pickupalert="early" role="note">
      <span className="pickupalerticon" aria-hidden="true">
        ⏱
      </span>{' '}
      <b>{line}</b>
    </div>
  )
}
